import { Anchor, Autocomplete, Flex, Icon, P } from '@dnb/eufemia';
import { AutocompleteOnTypeParams } from '@dnb/eufemia/components/autocomplete';
import { DrawerListDataArrayObjectStrict } from '@dnb/eufemia/fragments/drawer-list';
import { loupe_medium as LoupeIcon } from '@dnb/eufemia/icons';
import styled from '@emotion/styled';
import { UUID_OR_BA_ID_OR_BSN_ID_MATCHER } from '@portals/shared/common/matchers';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { backendClient } from '@/api/backendClient';
import { TypeBadge, typeToBadgeText } from '@/components/TypeBadge';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import { SearchResponseSearchResult as SearchResult } from '@/openapi/schema';
import { isEmptyString } from '@/utils/textUtils';

const SemanticSearchWrapper = styled(Flex.Horizontal)`
  .dnb-drawer-list__option__item--highlight {
    font-weight: normal;
    background: transparent;
  }
`;

const SemanticSearchContent = () => (
  <SemanticSearchWrapper
    align="center"
    aria-label="Navigate to semantic search page for more detailed results"
    justify="space-between"
  >
    <div>
      <P>Find answers using advanced search</P>
      <P size="small">Press Enter or click here for more results</P>
    </div>
    <Icon color="var(--color-sea-green)" icon={LoupeIcon} />
  </SemanticSearchWrapper>
);

const SEMANTIC_SEARCH_KEY = '__SEMANTIC_SEARCH__';

async function getResults(query: string): Promise<SearchResult[]> {
  if (isEmptyString(query)) return [];
  if (UUID_OR_BA_ID_OR_BSN_ID_MATCHER.test(query)) {
    const { data } = await backendClient.GET('/search/id', { params: { query: { id: query } } });
    return data?.results ?? [];
  } else {
    const { data } = await backendClient.GET('/search/fuzzy', { params: { query: { query } } });
    return data?.results ?? [];
  }
}

function getContent(result: SearchResult) {
  const name = result.name ?? `Unnamed ${typeToBadgeText[result.type]}`;
  const anchor = (
    <Anchor element={Link} to={result.path}>
      {name}
    </Anchor>
  );
  return isEmptyString(result.description) ? [anchor] : [anchor, result.description];
}

export function GlobalSearch() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(undefined);
  const latestQueryRef = useRef('');
  const { featureFlags } = useFeatureFlags();
  const semanticSearchEnabled = featureFlags.ENABLE_SEMANTIC_SEARCH ?? false;
  const [inputValue, setInputValue] = useState('');

  const shortcut = useMemo(() => {
    return navigator.userAgent.includes('Mac') ? '⌘K' : 'Ctrl+K';
  }, []);

  const onTypeHandler = useMemo(
    () => (event: AutocompleteOnTypeParams) => {
      const { value, showIndicator, hideIndicator, updateData, debounce, showNoOptionsItem } = event;
      setInputValue(value);
      latestQueryRef.current = value;
      showIndicator();
      debounce(
        async () => {
          const currentValue = latestQueryRef.current;
          const results = await getResults(currentValue);
          const newData: DrawerListDataArrayObjectStrict[] = results.map(
            (result): DrawerListDataArrayObjectStrict => ({
              groupIndex: 1,
              selectedKey: result.path,
              content: getContent(result),
              suffixValue: <TypeBadge type={result.type} />,
            }),
          );

          if (semanticSearchEnabled && !isEmptyString(currentValue)) {
            newData.unshift({
              groupIndex: 0,
              selectedKey: SEMANTIC_SEARCH_KEY,
              content: <SemanticSearchContent />,
            });
          }

          if (newData.length === 0) {
            showNoOptionsItem();
          } else {
            updateData(newData);
          }
          hideIndicator();
        },
        { value },
        300,
      );
    },
    [semanticSearchEnabled],
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }

      if (
        e.key === 'Enter' &&
        document.activeElement === inputRef.current &&
        semanticSearchEnabled &&
        !isEmptyString(inputValue)
      ) {
        e.preventDefault();
        setInputValue('');
        inputRef.current?.blur();
        navigate(`/search?q=${encodeURIComponent(inputValue)}`);
      }
    };

    globalThis.addEventListener('keydown', handleKeyDown);
    return () => globalThis.removeEventListener('keydown', handleKeyDown);
  }, [navigate, semanticSearchEnabled, inputValue]);

  return (
    <Autocomplete
      disableFilter
      groups={[undefined, 'Search results']}
      inputRef={inputRef}
      inputValue={inputValue}
      mode="async"
      noOptions="No results found."
      onSelect={({ value }) => {
        if (value === SEMANTIC_SEARCH_KEY) {
          if (isEmptyString(inputValue)) return;
          setInputValue('');
          navigate(`/search?q=${encodeURIComponent(inputValue)}`);
        } else if (value) {
          setInputValue('');
          navigate(String(value));
        }
      }}
      onType={onTypeHandler}
      placeholder={
        <div className="flex justify-between">
          <span className="text-emerald-green">Search ... </span>
          <span>({shortcut})</span>
        </div>
      }
      preventSelection
      size="medium"
      stretch
    />
  );
}
